import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useCfoDashboard } from "../hooks";
import type { CfoDashboardResponseDto } from "../cfo.types";

const CfoDashboardPage: React.FC = () => {
  const { data, isLoading, error } = useCfoDashboard();

  if (isLoading) {
    return (
      <PageIntro
        title="CFO Dashboard"
        message="Loading executive finance overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title="CFO Dashboard"
        message="Unable to load CFO dashboard. Please try again."
      />
    );
  }

  const safeData: CfoDashboardResponseDto = {
    kpis: {
      revenueGrowthPercent: data.kpis?.revenueGrowthPercent ?? 0,
      ebitdaMarginPercent: data.kpis?.ebitdaMarginPercent ?? 0,
      netProfitMarginPercent: data.kpis?.netProfitMarginPercent ?? 0,
      returnOnCapitalEmployedPercent:
        data.kpis?.returnOnCapitalEmployedPercent ?? 0,
    },
    cash: {
      operatingCashFlow: data.cash?.operatingCashFlow ?? "—",
      freeCashFlow: data.cash?.freeCashFlow ?? "—",
      cashBalance: data.cash?.cashBalance ?? "—",
      liquidityRunwayMonths: data.cash?.liquidityRunwayMonths ?? 0,
    },
    workingCapital: {
      debtorDays: data.workingCapital?.debtorDays ?? "—",
      inventoryDays: data.workingCapital?.inventoryDays ?? "—",
      creditorDays: data.workingCapital?.creditorDays ?? "—",
      workingCapitalRatio: data.workingCapital?.workingCapitalRatio ?? "—",
    },
    leverage: {
      debtToEquityRatio: data.leverage?.debtToEquityRatio ?? "—",
      netDebtToEbitdaRatio: data.leverage?.netDebtToEbitdaRatio ?? "—",
      covenantHeadroomPercent: data.leverage?.covenantHeadroomPercent ?? 0,
    },
    budgetVsActual: {
      revenueVariancePercent: data.budgetVsActual?.revenueVariancePercent ?? 0,
      expenseVariancePercent: data.budgetVsActual?.expenseVariancePercent ?? 0,
      capexVsBudgetPercent: data.budgetVsActual?.capexVsBudgetPercent ?? 0,
    },
  };

  const { kpis, cash, workingCapital, leverage, budgetVsActual } = safeData;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title="CFO Dashboard"
        message="Executive view of financial performance, liquidity, leverage, and budget variance."
      />

      <SectionCard title="Key performance indicators">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Revenue growth"
            value={`${kpis.revenueGrowthPercent.toFixed(1)}%`}
          />
          <StatCard
            label="EBITDA margin"
            value={`${kpis.ebitdaMarginPercent.toFixed(1)}%`}
          />
          <StatCard
            label="Net profit margin"
            value={`${kpis.netProfitMarginPercent.toFixed(1)}%`}
          />
          <StatCard
            label="ROCE"
            value={`${kpis.returnOnCapitalEmployedPercent.toFixed(1)}%`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Cash & liquidity">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Operating cash flow"
            value={cash.operatingCashFlow}
          />
          <StatCard label="Free cash flow" value={cash.freeCashFlow} />
          <StatCard label="Cash balance" value={cash.cashBalance} />
          <StatCard
            label="Liquidity runway"
            value={`${cash.liquidityRunwayMonths} months`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Working capital efficiency">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Debtor days (DSO)"
            value={workingCapital.debtorDays}
          />
          <StatCard
            label="Inventory days"
            value={workingCapital.inventoryDays}
          />
          <StatCard
            label="Creditor days (DPO)"
            value={workingCapital.creditorDays}
          />
          <StatCard
            label="Working capital ratio"
            value={workingCapital.workingCapitalRatio}
          />
        </div>
      </SectionCard>

      <SectionCard title="Leverage & covenants">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Debt / equity"
            value={leverage.debtToEquityRatio}
          />
          <StatCard
            label="Net debt / EBITDA"
            value={leverage.netDebtToEbitdaRatio}
          />
          <StatCard
            label="Covenant headroom"
            value={`${leverage.covenantHeadroomPercent.toFixed(1)}%`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Budget vs actual">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Revenue variance"
            value={`${budgetVsActual.revenueVariancePercent.toFixed(1)}%`}
          />
          <StatCard
            label="Expense variance"
            value={`${budgetVsActual.expenseVariancePercent.toFixed(1)}%`}
          />
          <StatCard
            label="Capex vs budget"
            value={`${budgetVsActual.capexVsBudgetPercent.toFixed(1)}%`}
          />
        </div>
      </SectionCard>
    </div>
  );
};

export default CfoDashboardPage;